import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, gql } from '@apollo/client';
import { Button, Skeleton, Stack } from '@mui/material'

import Select from 'components/global/CUK/AsyncSelect/Select'
import { toast } from 'components/global/CUK/Toast/Toast';
import { trackEvent } from 'analytics';

import {Header} from 'components/Modal';
import { useRoom } from '../../use-rooms';

interface Spare {
  uuid: string;
  serial: string;
  kind: string;
}

const OPERATION_FIND_SPARES = gql`
  query GetSpares($sf_ids: String!) {
    spares: findSpares(sf_ids: $sf_ids) {
      uuid
      kind
      mac
      serial
    }
  }
`

const OPERATION_REPLACE_TV = gql`
  mutation replaceDevice(
    $sf_id: String!
    $device_uuid: String!
    $new_device_uuid: String!
  ) {
    device: replaceTvBySpare(
      sf_id: $sf_id
      device_uuid: $device_uuid
      new_device_uuid: $new_device_uuid
    ) {
      ... on TV {
        uuid
        serial
        mac
        kind
      }
    }
  }
`

const OPERATION_REPLACE_CHROMECAST = gql`
  mutation replaceDevice(
    $sf_id: String!
    $device_uuid: String!
    $new_device_uuid: String!
  ) {
    device: replaceChromecastBySpare(
      sf_id: $sf_id
      device_uuid: $device_uuid
      new_device_uuid: $new_device_uuid
    ) {
      ... on Chromecast {
        uuid
        serial
        mac
        kind
      }
    }
  }
`

const Spare = ({ onCancel, onSuccess, kind = 'tv' }) => {
  const [spare, setSpare] = useState<Spare | null>(null);
  const room = useRoom()
  const { t } = useTranslation();
  const { deviceId } = useParams();
  console.log('room', room)
  const { loading, error, data } = useQuery(OPERATION_FIND_SPARES, {
    fetchPolicy: 'no-cache',
    variables: {
      sf_ids: room?.site_sf_id,
    },
    skip: !room
  });
  console.log(error)
  const [replaceDevice, { loading: loadingReplace }] = useMutation(
    kind === 'tv' ? OPERATION_REPLACE_TV : OPERATION_REPLACE_CHROMECAST,
    {
      onCompleted: (data) => {
        trackEvent('stream_cast_device_replaced', { replacement: 'spare', device_kind: kind });
        toast.success(t('stream-cast.actions.replace.toast_success'));
        onSuccess(data.device.uuid);
      },
      onError: () => {
        trackEvent('stream_cast_action_failed', { action: 'replace', replacement: 'spare', device_kind: kind });
        toast.error(t('stream-cast.actions.replace.toast_error'));
      },
      refetchQueries: ['GetRooms']
    }
    
  )

  const handleConfirm = async () => {
    if (spare?.uuid && deviceId?.toString()) {
      await replaceDevice({
        variables: {
          sf_id: room?.site_sf_id,
          device_uuid: deviceId?.toString(),
          new_device_uuid: spare.uuid,
        },
      });
    }
  };

  return (
    <Stack gap={2} sx={{ height: '100%' }}>
      <Header icon={<i className="fa-light fa-arrow-left-arrow-right" />} title={t('stream-cast.actions.replace_spare.title')} />
      <Stack justifyContent="space-between" sx={{ height: '100%' }}>
        {
          error
            ? t('ui_elements.error')
            : !room
              ? <Skeleton variant={'rectangular'} width={'100%'} height={32} sx={{borderRadius: 1}}/>
              : <Select
                  /* @ts-expect-error not jsx */
                  keyField="uuid"
                  mainField="serial"
                  menuHeight="160px"
                  value={spare}
                  onChange={(spare) => setSpare(spare)}
                  options={loading ? [] : data.spares.filter((spare) => spare.kind === 'tv')}
                  defaultPlaceholder={loading ? t('ui_elements.loading') : t('stream-cast.actions.replace.select_spare')}
                  noOptionsMessage={t('stream-cast.actions.replace.no_options_spares')}
                />
        }

        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={onCancel} size="small">
            {t('stream-cast.actions.cancel')}
          </Button>
          <Button variant="contained" color="primary" disabled={loadingReplace || !spare} onClick={handleConfirm} size="small">
            {t('stream-cast.actions.submit')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Spare;
